/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import Cookies from 'js-cookie';
import 'firebase/auth';
import { useHistory } from 'react-router-dom';
import signIn from 'api/user/signin';

import * as styles from 'components/pages/signin/signin.stlyes';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const Signin = () => {
  const history = useHistory();
  const [errMessage, setErrMessage] = useState({
    show: false,
    text: '',
  });
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
    } else {
      firebase.app(); // if already initialized, use that one
    }
  }, []);

  const resolveErrorMessage = (error) => (
    error?.response?.data?.message
    || error?.response?.data?.errors?.[0]?.detail
    || error?.message
    || 'Failed to sign in. Please try again.'
  );

  const googleSignin = async () => {
    setErrMessage({
      show: false,
      text: '',
    });
    setLoading(true);
    const provider = new firebase.auth.GoogleAuthProvider();
    try {
      const { user } = await firebase.auth().signInWithPopup(provider);
      firebase.auth().onAuthStateChanged(async (u) => {
        try {
          if (u) {
            if (firebase.auth().currentUser) {
              const token = await firebase.auth().currentUser.getIdToken();
              const rToken = await firebase.auth().currentUser.refreshToken;
              const payload = {
                grant_type: 'mfa_google',
                token_id: token,
                refresh_token: rToken,
              };
              const { data } = await signIn(payload);
              Cookies.set('pic', user.email);
              Cookies.set('access_token', data.data.access_token);
              Cookies.set('refresh_token', data.data.refresh_token);
              if (data.code === 200) {
                history.push('/ftp/applicants');
                return;
              }
            }
          }
          setLoading(false);
        } catch (error) {
          setLoading(false);
          setErrMessage({
            show: true,
            text: resolveErrorMessage(error),
          });
        }
      });
    } catch (error) {
      setLoading(false);
      setErrMessage({
        show: true,
        text: resolveErrorMessage(error),
      });
    }
  };

  return (
    <div css={styles.pageStyles}>
      <aside css={styles.brandPanelStyles}>
        <img src="/h8.jpeg" alt="Hacktiv8" width="56" height="56" css={styles.logoStyles} />
        <div>
          <span css={styles.accentStyles} />
          <h1 css={styles.brandTitleStyles}>Admission Backoffice</h1>
          <p css={styles.brandCopyStyles}>
            Manage applicants, batches and students for Full Time &amp; Part Time programs.
          </p>
        </div>
        <span css={styles.brandFooterStyles}>Hacktiv8 Indonesia</span>
      </aside>
      <main css={styles.formPanelStyles}>
        <div css={styles.cardStyles}>
          <h2 css={styles.titleStyles}>Sign in</h2>
          <p css={styles.hintStyles}>Use your Hacktiv8 Google account</p>
          <button
            type="button"
            css={styles.googleButtonStyles}
            onClick={googleSignin}
            disabled={isLoading}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            <span>{isLoading ? 'Signing in…' : 'Sign in with Google'}</span>
          </button>
          {
            errMessage.show && (
              <div role="alert" css={styles.errorStyles}>
                {errMessage.text}
              </div>
            )
          }
        </div>
      </main>
    </div>
  );
};

export default Signin;
